import { Injectable, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from '../store';
import { selectTenantInfo } from '../store/selectors';

/**
 * The i18n key every "Subscribe to Access All Products" button on the app reads.
 *
 * Rendered on FIVE surfaces: the course page (three separate blocks), the redesigned sales
 * page, and the course embed's pricing panel.
 */
export const COURSE_SUBSCRIBE_KEY = 'course.courseActionButtons.subscribe';

/**
 * Lets a school reword the course-page subscribe button, which the product owner asked for
 * alongside the per-plan card button.
 *
 * WHY IT OVERRIDES THE TRANSLATION RATHER THAN CHANGING FIVE TEMPLATES
 *
 * The label is one i18n key rendered from five places. The alternatives were to thread a
 * tenant value into five components (five sets of imports, five chances to miss one, and a
 * sixth surface added later silently missing it), or to change what that key resolves to.
 * Overriding the key covers every current AND future render site by construction.
 *
 * This is safe here in a way it would not be in a general-purpose app: the platform serves
 * exactly ONE tenant per origin - the subdomain decides the tenant before the app boots -
 * so there is no second school whose label could leak into this one's page.
 *
 * The override is re-applied whenever the active language changes, because ngx-translate
 * loads each language's table separately and a table loaded after we wrote would otherwise
 * put the stock wording back.
 *
 * An empty or absent value leaves the translation alone, which is what keeps all 30 locales
 * working for the schools that never set one.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionLabelsService {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly translate = inject(TranslateService);

  private readonly tenantSig = toSignal(this.store.pipe(select(selectTenantInfo)));

  /**
   * The stock translation of the button, captured PER LANGUAGE before we overwrite it.
   *
   * This was a single `stockLabel: string | null` captured once, from whichever language
   * happened to be active first - and then written into every language the visitor switched
   * to afterwards. A school with no custom label had its Spanish, Japanese and Arabic course
   * pages relabelled with the ENGLISH wording the moment anyone changed language. That hit
   * every school on the platform, including the overwhelming majority that get no benefit
   * from this feature at all.
   *
   * Keyed by language, each entry is that language's own wording, so restoring is exact.
   */
  private readonly stockLabels = new Map<string, string>();

  constructor() {
    // Re-apply on language change as well as on tenant change.
    this.translate.onLangChange.subscribe(() => this.apply(this.currentLabel()));

    effect(() => {
      this.apply(this.currentLabel());
    });
  }

  private currentLabel(): string {
    return (this.tenantSig() as any)?.subscriptionButtonLabel?.trim() ?? '';
  }

  private apply(custom: string): void {
    const lang = this.translate.currentLang || this.translate.getDefaultLang();
    if (!lang) {
      return;
    }

    if (!this.stockLabels.has(lang)) {
      const stock = this.translate.instant(COURSE_SUBSCRIBE_KEY);
      // `instant` echoes the key back when the table has not loaded yet - storing that as
      // "the stock wording" would permanently replace the button with a dotted key path.
      // Left uncaptured, it is retried on the next apply for this language.
      if (stock && stock !== COURSE_SUBSCRIBE_KEY) {
        this.stockLabels.set(lang, stock);
      }
    }

    // THIS language's own stock wording - never another language's.
    const value = custom || this.stockLabels.get(lang);
    if (!value) {
      return;
    }

    // `setTranslation(..., merge = true)` rather than `set`, so only this one key is
    // touched and the rest of the loaded table is left exactly as it was.
    this.translate.setTranslation(
      lang,
      { course: { courseActionButtons: { subscribe: value } } },
      true
    );
  }
}
